import { useState } from 'react';
import { cx } from '../primitives/cx.js';
import { Icon } from '../primitives/Icon.tsx';
import { Num } from '../primitives/Num.tsx';
import { InlineAction } from '../primitives/Button.tsx';

/**
 * Timeline - the main column of every record page.
 *
 * Two things this component exists to get right.
 *
 * **An agent entry looks like an agent entry.** A distinct icon, a muted
 * ground, and always a link to the reasoning. A person reading a record has to
 * be able to tell at a glance what was done by a colleague and what was done
 * by software on their behalf, and if they cannot, they will eventually
 * discover it at the worst possible moment.
 *
 * **Entries are one line until asked.** A timeline where every entry is
 * expanded is a wall, and a wall is not read. Expansion happens in place -
 * never in a dialog, because the point of the entry is its position among the
 * others.
 */

export interface TimelineEntry {
  id: string;
  type: string;
  typeLabel: string;
  typeIcon: string;
  subject: string;
  body: string | null;
  /** Already through the formatter. */
  whenLabel: string;
  actorName: string | null;
  actorType: 'user' | 'agent' | 'system';
  /** Present on agent entries. Never absent on one. */
  reasoningHref: string | null;
}

export interface TimelineLabels {
  /** The chip that clears the type filter. */
  all: string;
  /** Announced on an agent entry, in words as well as by icon and ground. */
  byAgent: string;
  bySystem: string;
  whyDidThis: string;
  empty: string;
  emptyAction: string;
  expand: string;
  collapse: string;
  loadMore: string;
  showing: (shown: string, total: string) => string;
}

export interface TimelineProps {
  entries: TimelineEntry[];
  /** Derived from what is on the record, with counts. Never a written list. */
  types: Array<{ type: string; label: string; count: number }>;
  selectedTypes: string[];
  onTypesChange: (types: string[]) => void;
  total: string;
  shown: string;
  hasMore: boolean;
  onLoadMore: () => void;
  labels: TimelineLabels;
  empty?: React.ReactNode;
}

export function Timeline({
  entries,
  types,
  selectedTypes,
  onTypesChange,
  total,
  shown,
  hasMore,
  onLoadMore,
  labels,
  empty,
}: TimelineProps): JSX.Element {
  return (
    <div data-component="Timeline" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <FilterChip
          label={labels.all}
          count={null}
          active={selectedTypes.length === 0}
          onClick={() => onTypesChange([])}
        />
        {types.map((type) => (
          <FilterChip
            key={type.type}
            label={type.label}
            count={String(type.count)}
            active={selectedTypes.includes(type.type)}
            onClick={() =>
              onTypesChange(
                selectedTypes.includes(type.type)
                  ? selectedTypes.filter((t) => t !== type.type)
                  : [...selectedTypes, type.type],
              )
            }
          />
        ))}
        <Num className="ms-auto text-meta text-ink-meta">{labels.showing(shown, total)}</Num>
      </div>

      {entries.length === 0 ? (
        (empty ?? <p className="text-body text-ink-meta">{labels.empty}</p>)
      ) : (
        <ol className="flex flex-col">
          {entries.map((entry) => (
            <TimelineRow key={entry.id} entry={entry} labels={labels} />
          ))}
        </ol>
      )}

      {hasMore ? (
        <InlineAction data-load-more onClick={onLoadMore}>
          {labels.loadMore}
        </InlineAction>
      ) : null}
    </div>
  );
}

function FilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: string | null;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      data-filter-chip={label}
      aria-pressed={active}
      onClick={onClick}
      className={cx(
        'inline-flex h-chip items-center gap-1 rounded-chip px-2 text-meta',
        'focus-visible:outline-none',
        active ? 'bg-bronze-tint text-bronze-text' : 'bg-sunken text-ink-600 hover:text-ink',
      )}
    >
      {label}
      {count === null ? null : <Num className="text-ink-meta">{count}</Num>}
    </button>
  );
}

function TimelineRow({ entry, labels }: { entry: TimelineEntry; labels: TimelineLabels }): JSX.Element {
  const [open, setOpen] = useState(false);
  const automated = entry.actorType !== 'user';

  return (
    <li
      data-timeline-entry={entry.id}
      data-actor-type={entry.actorType}
      className={cx(
        'flex gap-3 border-s-2 py-2 ps-3',
        // Three carriers for the same fact: the icon, the ground, and the
        // sentence below. A distinction made only in colour is not a
        // distinction for everybody.
        automated ? 'border-s-bronze-rule bg-sunken' : 'border-s-rule',
      )}
    >
      <span className={cx('mt-0.5', automated ? 'text-bronze-text' : 'text-ink-400')}>
        <Icon name={automated ? 'Sparkle' : entry.typeIcon} size="inline" decorative />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex flex-wrap items-baseline gap-2">
          <button
            type="button"
            data-expand-entry={entry.id}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="min-w-0 flex-1 truncate text-start text-body text-ink hover:text-bronze-text"
          >
            {entry.subject}
          </button>
          <Num className="text-meta text-ink-meta">{entry.whenLabel}</Num>
        </div>

        <p className="flex flex-wrap items-center gap-2 text-meta text-ink-meta">
          <span>
            {entry.actorType === 'agent'
              ? labels.byAgent
              : entry.actorType === 'system'
                ? labels.bySystem
                : (entry.actorName ?? '')}
          </span>
          {/* An agent entry always carries its reasoning. An automated action a
              person cannot interrogate is an automated action they cannot
              trust or correct. */}
          {entry.actorType === 'agent' && entry.reasoningHref ? (
            <a
              href={entry.reasoningHref}
              data-reasoning-link
              className="text-bronze-text underline decoration-bronze-rule underline-offset-4"
            >
              {labels.whyDidThis}
            </a>
          ) : null}
        </p>

        {/* Expanded in place: the entry's position among the others is half of
            what it means, and a dialog throws that away. */}
        {open && entry.body ? (
          <p className="whitespace-pre-wrap pt-1 text-dense text-ink">{entry.body}</p>
        ) : null}
      </div>
    </li>
  );
}
